'use client';

import { IT_INT } from '@slims/shared/constants';
import {
	Bar,
	CartesianGrid,
	ComposedChart,
	Line,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts';

export interface ConsumptionTrendPoint {
	/** Etichetta asse X, es. `lug 26`. */
	label: string;
	/** Consumo del mese, kWh. */
	prelevata: number;
	/** Stesso mese dell'anno precedente, `null` se fuori storico. */
	prevYearPrelevata?: number | null;
}

export interface ConsumptionTrendChartProps {
	data: ConsumptionTrendPoint[];
	/** Testo mostrato al posto del grafico quando non c'è storico. */
	emptyMessage?: string;
}

/**
 * Trend consumi mensili: barre del periodo, linea tratteggiata dello stesso mese
 * dell'anno precedente. La linea si interrompe sui mesi senza confronto, invece
 * di scendere a zero (`connectNulls` off).
 */
export function ConsumptionTrendChart({ data, emptyMessage }: ConsumptionTrendChartProps) {
	const hasPrevYear = data.some((d) => d.prevYearPrelevata != null);

	if (data.length === 0) {
		return (
			<div className="flex h-[220px] items-center justify-center">
				<span className="rounded-md bg-bg-card/80 px-3 py-1.5 text-[13px] text-text-muted">
					{emptyMessage ?? 'Nessun dato di consumo disponibile'}
				</span>
			</div>
		);
	}

	return (
		<ResponsiveContainer width="100%" height={220}>
			<ComposedChart data={data}>
				<CartesianGrid stroke="#1a3a5c" strokeDasharray="3 3" vertical={false} />
				<XAxis
					dataKey="label"
					tick={{ fontSize: 11, fill: '#64748b' }}
					tickLine={false}
					axisLine={false}
				/>
				<YAxis
					tick={{ fontSize: 11, fill: '#64748b' }}
					tickLine={false}
					axisLine={false}
					unit=" kWh"
					width={62}
				/>
				<Tooltip
					contentStyle={{
						background: '#0f1f35',
						border: '1px solid #1a3a5c',
						borderRadius: 8,
						fontSize: 13,
					}}
					labelStyle={{ color: '#e2e8f0' }}
					formatter={(value, name) => [
						`${Math.round(Number(value)).toLocaleString('it-IT', IT_INT)} kWh`,
						name,
					]}
				/>
				<Bar
					dataKey="prelevata"
					name="Consumo"
					fill="var(--color-accent-purple)"
					radius={[3, 3, 0, 0]}
				/>
				{hasPrevYear && (
					<Line
						type="monotone"
						dataKey="prevYearPrelevata"
						name="Anno precedente"
						stroke="var(--color-accent)"
						strokeWidth={2}
						strokeDasharray="5 4"
						dot={false}
						connectNulls={false}
					/>
				)}
			</ComposedChart>
		</ResponsiveContainer>
	);
}
ConsumptionTrendChart.displayName = 'ConsumptionTrendChart';
